/**
 * What a product line covers, in one wording for the line card and the live preview (Y3).
 *
 * The backend puts every SKU a line matches in exactly one bucket, the same way for both
 * (`merchant_product_lines.match_breakdown` at the last re-match, `breakdown` in the
 * preview; migration 20261031300001):
 *   rejected  the merchant rejected it in the approval queue
 *   in_store  already sold regardless of the line: hand-picked in a supplier catalog, or
 *             from a supplier set to "Select all" (the line only adds its tag)
 *   added     selected because of this line (auto line, or approved)
 *   waiting   in the approval queue
 */
export interface LineBreakdown {
  in_store: number;
  added: number;
  waiting: number;
  rejected: number;
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);

/** Null when the backend sent no breakdown (line not re-matched since the migration). */
export function readBreakdown(raw: unknown): LineBreakdown | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return { in_store: num(r.in_store), added: num(r.added), waiting: num(r.waiting), rejected: num(r.rejected) };
}

export const IN_STORE_HINT =
  'Already for sale in your store: picked by hand in a supplier catalog, or from a supplier set to "Select all". The line adds its tag.';

/**
 * "1 already in your store · 1 waiting for approval". Only the parts that are non-zero; a
 * line that only adds products reads "51 products" as before.
 */
export function describeLineCounts(b: LineBreakdown | null, fallbackTotal: number): string {
  if (!b) return `${fallbackTotal} products`;
  const parts: string[] = [];
  if (b.in_store) parts.push(`${b.in_store} already in your store`);
  if (b.added) parts.push(b.in_store || b.waiting || b.rejected ? `${b.added} added by this line` : `${b.added} products`);
  if (b.waiting) parts.push(`${b.waiting} waiting for approval`);
  if (b.rejected) parts.push(`${b.rejected} rejected`);
  return parts.length ? parts.join(' · ') : '0 products';
}
